import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { getCountryRecords, getCountryName } from "./countries";

const parseSafeAreaValue = (value) => {
  const numeric = parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getSafeAreaInsets = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  const style = window.getComputedStyle(document.documentElement);
  return {
    top: parseSafeAreaValue(style.getPropertyValue("--app-safe-area-top")),
    right: parseSafeAreaValue(style.getPropertyValue("--app-safe-area-right")),
    bottom: parseSafeAreaValue(style.getPropertyValue("--app-safe-area-bottom")),
    left: parseSafeAreaValue(style.getPropertyValue("--app-safe-area-left")),
  };
};

const Flag = ({ code, size = 20, className = "" }) =>
  code ? (
    <img
      src={`https://flagcdn.com/${size === 24 ? "24x18" : "20x15"}/${code}.png`}
      alt=""
      width={size === 24 ? 24 : 20}
      height={size === 24 ? 18 : 15}
      className={className}
      style={{ display: "block", borderRadius: 2, objectFit: "cover" }}
      loading="lazy"
    />
  ) : null;

const detectMobileOrTouch = () => {
  if (typeof window === "undefined") return false;
  if ("ontouchstart" in window) return true;
  if (typeof navigator !== "undefined") {
    if (navigator.maxTouchPoints > 0) return true;
    if ("msMaxTouchPoints" in navigator && navigator.msMaxTouchPoints > 0) return true;
  }
  const width = window.innerWidth || 0;
  try {
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    return coarse || width < 768;
  } catch (err) {
    return width < 768;
  }
};

const buildSelectStyles = (z, menuRect, menuOpen) => ({
  container: (base) => ({
    ...base,
    width: "100%",
    fontSize: "16px",
  }),
  control: (base, state) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    lineHeight: "1",
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
    borderRadius: "0.75rem",
    borderColor: state.isFocused ? "var(--focus-border)" : "var(--bs-border-color)",
    minHeight: 44,
    height: 44,
    width: "100%",
    cursor: "pointer",
    backgroundColor: "var(--bs-body-bg)",
    color: "var(--bs-body-color)",
    fontSize: "16px",
    transition: "border-color 0.15s ease",
    ":hover": {
      borderColor: state.isFocused ? "var(--focus-border)" : "var(--bs-border-color)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: 8,
    padding: "0 14px",
    cursor: "pointer",
    minHeight: 44,
    fontSize: "16px",
    lineHeight: "1",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    paddingRight: 8,
    height: 44,
    gap: 4,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    padding: 6,
  }),
  clearIndicator: (base) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    padding: 6,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: z,
    pointerEvents: menuOpen ? "auto" : "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: z,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "var(--bs-body-bg)",
    color: "var(--bs-body-color)",
    border: "1px solid var(--bs-border-color)",
    position: "fixed",
    top: menuRect?.top ?? base.top,
    left: menuRect?.left ?? base.left,
    width: menuRect?.width ?? base.width,
    minWidth: menuRect?.width ?? base.minWidth,
    marginTop: 0,
    transform: "translate3d(0,0,0)",
    maxHeight: menuRect?.maxHeight ?? base.maxHeight ?? 280,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: "var(--bs-body-bg)",
    color: "var(--bs-body-color)",
    maxHeight: menuRect?.maxHeight ?? base.maxHeight ?? 280,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    paddingBottom: (menuRect?.safeAreaBottom ?? 0) + 8,
  }),
  option: (base, state) => ({
    ...base,
    height: 36,
    display: "flex",
    alignItems: "center",
    backgroundColor: state.isSelected
      ? "var(--bs-primary-bg-subtle)"
      : state.isFocused
        ? "var(--bs-tertiary-bg)"
        : "var(--bs-body-bg)",
    color: state.isSelected ? "var(--bs-primary-text-emphasis)" : "var(--bs-body-color)",
    ":active": {
      backgroundColor: "var(--bs-primary-bg-subtle)",
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--bs-body-color)",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    maxWidth: "100%",
    lineHeight: "1",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--bs-secondary-color)",
    margin: 0,
    fontSize: "16px",
    lineHeight: "1",
    whiteSpace: "nowrap",
  }),
  input: (base) => ({
    ...base,
    color: "var(--bs-body-color)",
    margin: 0,
    fontSize: "16px",
    lineHeight: "1",
    padding: 0,
  }),
});

const OptionWithFlag = (props) => {
  const { data, innerProps, isFocused } = props;
  return (
    <div
      {...innerProps}
      className="d-flex align-items-center px-2"
      style={{
        height: 36,
        backgroundColor: isFocused ? "var(--bs-tertiary-bg)" : "transparent",
      }}
    >
      <Flag code={data.code} size={24} className="me-2" />
      <span>{data.label}</span>
    </div>
  );
};

const SingleValueWithFlag = ({ data }) => (
  <div className="d-flex align-items-center" style={{ minWidth: 0, lineHeight: "1" }}>
    <Flag code={data.code} size={24} className="me-2" />
    <span
      style={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flexGrow: 1,
        lineHeight: "1",
      }}
    >
      {data.label}
    </span>
  </div>
);

export default function CountrySelect({
  value,
  onChange,
  locked = false,
  placeholder = "Select a country",
  showFlagInValue = true,
  className = "",
  isClearable = true,
  isLoading = false,
  allowedCodes,
  menuZIndex = 2600,
  menuPortalTarget = typeof document !== "undefined" ? document.body : null,
  onInputChange: onInputChangeProp,
  onMenuOpen: onMenuOpenProp,
  onMenuClose: onMenuCloseProp,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  onKeyDown: onKeyDownProp,
  isSearchable: isSearchableProp,
  ...rest
}) {
  const selectRef = useRef(null);
  const rafRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const [isTouchInput, setIsTouchInput] = useState(() => detectMobileOrTouch());

  const selectStyles = useMemo(
    () => buildSelectStyles(menuZIndex, menuRect, menuOpen),
    [menuZIndex, menuRect, menuOpen]
  );
  const all = useMemo(getCountryRecords, []);
  const normalizeValue = useMemo(() => (val) => {
    if (!val) return "";
    const trimmed = String(val).trim();
    if (/^[a-z]{2}$/i.test(trimmed)) return trimmed.toLowerCase();
    return "";
  }, []);
  const allow = useMemo(() => {
    if (!allowedCodes) return null;
    const arr = Array.isArray(allowedCodes) ? allowedCodes : Array.from(allowedCodes);
    return new Set(arr.map((c) => String(c || "").toLowerCase()));
  }, [allowedCodes]);

  const options = useMemo(() => {
    if (!allow) return all;
    return all.filter((o) => allow.has((o.code || "").toLowerCase()));
  }, [all, allow]);

  const selectClassName = ["w-100", className].filter(Boolean).join(" ").trim();

  const selected = useMemo(() => {
    if (!value) return null;
    const normalized = normalizeValue(value);
    if (normalized) {
      const match = options.find((o) => (o.code || "").toLowerCase() === normalized);
      if (match) return match;
      const label = getCountryName(normalized) || normalized.toUpperCase();
      return { value: normalized, label, code: normalized };
    }
    const byLabel = options.find((o) => o.label === value);
    return byLabel || { value, label: value, code: "" };
  }, [value, options, normalizeValue]);

  const cancelRaf = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => setIsTouchInput(detectMobileOrTouch());
    update();

    let mql;
    try {
      mql = window.matchMedia("(pointer: coarse)");
    } catch (err) {
      mql = null;
    }

    if (mql?.addEventListener) {
      mql.addEventListener("change", update);
    } else if (mql?.addListener) {
      mql.addListener(update);
    }

    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
      if (!mql) return;
      if (mql.removeEventListener) {
        mql.removeEventListener("change", update);
      } else if (mql.removeListener) {
        mql.removeListener(update);
      }
    };
  }, []);

  const ensureControlVisible = useCallback(() => {
    if (typeof window === "undefined") return;
    const control = selectRef.current?.controlRef;
    if (!control) return;
    const rect = control.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const offsetTop = viewport?.offsetTop ?? 0;
    const insets = getSafeAreaInsets();
    const padding = 12;
    const topLimit = offsetTop + insets.top + padding;
    const bottomLimit = offsetTop + viewportHeight - insets.bottom - padding;
    if (rect.bottom > bottomLimit) {
      const delta = rect.bottom - bottomLimit;
      window.scrollBy({ top: delta, behavior: "smooth" });
    } else if (rect.top < topLimit) {
      const delta = rect.top - topLimit;
      window.scrollBy({ top: delta, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    ensureControlVisible();
  }, [menuOpen, ensureControlVisible]);

  const updateMenuMetrics = useCallback(() => {
    if (!menuOpen) return;
    const control = selectRef.current?.controlRef;
    if (!control) return;
    const rect = control.getBoundingClientRect();
    if (!rect) return;
    const hasWindow = typeof window !== "undefined";
    const viewport = hasWindow ? window.visualViewport : null;
    const viewportHeight = hasWindow ? viewport?.height ?? window.innerHeight : rect.bottom + 320;
    const viewportWidth = hasWindow ? viewport?.width ?? window.innerWidth : rect.right + 320;
    const offsetLeft = viewport?.offsetLeft ?? 0;
    const offsetTop = viewport?.offsetTop ?? 0;
    const insets = getSafeAreaInsets();
    const margin = 8;
    const availableBelow = Math.max(
      0,
      viewportHeight - (rect.bottom + offsetTop) - margin - insets.bottom
    );
    const limit = Math.max(220, viewportHeight - margin * 2 - insets.top - insets.bottom);
    const targetHeight = Math.min(320, limit);
    const maxHeight = Math.max(
      180,
      Math.min(availableBelow > 0 ? availableBelow : targetHeight, limit)
    );
    const desiredTop = rect.bottom + offsetTop + margin;
    const maxTop = Math.max(margin + insets.top, viewportHeight - insets.bottom - margin - maxHeight);
    const top = Math.min(desiredTop, maxTop);

    const rawLeft = rect.left + offsetLeft;
    const maxLeft = viewportWidth - insets.right - margin - rect.width;
    const left = Math.min(
      Math.max(rawLeft, insets.left + margin),
      Math.max(maxLeft, insets.left + margin)
    );
    const width = Math.min(rect.width, viewportWidth - insets.left - insets.right - margin * 2);

    if (rect.bottom < 0 || rect.top > viewportHeight) {
      selectRef.current?.blur?.();
      return;
    }
    const next = {
      width,
      left,
      top,
      topEdge: rect.top,
      maxHeight,
      safeAreaBottom: insets.bottom,
    };
    setMenuRect((prev) => {
      if (
        !prev ||
        prev.width !== next.width ||
        prev.left !== next.left ||
        prev.top !== next.top ||
        prev.maxHeight !== next.maxHeight
      ) {
        return next;
      }
      return prev;
    });
  }, [menuOpen]);

  const scheduleUpdate = useCallback(() => {
    cancelRaf();
    if (typeof window === "undefined") return;
    rafRef.current = window.requestAnimationFrame(updateMenuMetrics);
  }, [cancelRaf, updateMenuMetrics]);

  useEffect(
    () => () => {
      cancelRaf();
    },
    [cancelRaf]
  );

  useEffect(() => {
    if (!menuOpen) return undefined;
    scheduleUpdate();
    const hasWindow = typeof window !== "undefined";
    const viewport = hasWindow ? window.visualViewport : null;
    const handler = () => scheduleUpdate();
    const scrollHandler = () => scheduleUpdate();

    if (viewport) {
      viewport.addEventListener("resize", handler);
      viewport.addEventListener("scroll", handler);
    }
    if (hasWindow) {
      window.addEventListener("resize", handler);
      window.addEventListener("scroll", scrollHandler, true);
    }

    return () => {
      if (viewport) {
        viewport.removeEventListener("resize", handler);
        viewport.removeEventListener("scroll", handler);
      }
      if (hasWindow) {
        window.removeEventListener("resize", handler);
        window.removeEventListener("scroll", scrollHandler, true);
      }
    };
  }, [menuOpen, scheduleUpdate]);

  const handleMenuOpen = useCallback(() => {
    setMenuOpen(true);
    scheduleUpdate();
    const control = selectRef.current?.controlRef;
    if (control) {
      control.scrollIntoView?.({ block: "center", inline: "nearest", behavior: "smooth" });
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => ensureControlVisible());
      }
    }
    onMenuOpenProp?.();
  }, [scheduleUpdate, onMenuOpenProp, ensureControlVisible]);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    setMenuRect(null);
    cancelRaf();
    onMenuCloseProp?.();
  }, [cancelRaf, onMenuCloseProp]);

  const handleInputChange = useCallback(
    (inputValue, meta) => {
      scheduleUpdate();
      if (onInputChangeProp) return onInputChangeProp(inputValue, meta);
      return inputValue;
    },
    [scheduleUpdate, onInputChangeProp]
  );

  const handleFocus = useCallback(
    (event) => {
      scheduleUpdate();
      const control = selectRef.current?.controlRef;
      control?.scrollIntoView?.({ block: "nearest", inline: "nearest", behavior: "smooth" });
      onFocusProp?.(event);
    },
    [scheduleUpdate, onFocusProp]
  );

  const handleBlur = useCallback(
    (event) => {
      setMenuOpen(false);
      setMenuRect(null);
      cancelRaf();
      onBlurProp?.(event);
    },
    [cancelRaf, onBlurProp]
  );

  const handleKeyDown = useCallback(
    (event) => {
      scheduleUpdate();
      onKeyDownProp?.(event);
    },
    [scheduleUpdate, onKeyDownProp]
  );

  const isSearchable = isSearchableProp ?? !isTouchInput;

  if (locked) {
    return (
      <div
        className={`form-control bg-body text-body d-flex align-items-center gap-2 ${className}`}
        style={{ height: 40, fontSize: "16px" }}
      >
        <Flag code={selected?.code} />
        <span>{selected?.label || "—"}</span>
      </div>
    );
  }

  return (
    <Select
      className={selectClassName || undefined}
      classNamePrefix="rs"
      inputId="country"
      instanceId="country"
      placeholder={placeholder}
      options={options}
      value={selected}
      onChange={(opt) => onChange?.(opt?.code || opt?.value || "")}
      isClearable={isClearable}
      isLoading={isLoading}
      isSearchable={isSearchable}
      styles={selectStyles}
      components={{
        Option: OptionWithFlag,
        SingleValue: showFlagInValue ? SingleValueWithFlag : undefined,
        IndicatorSeparator: null,
      }}
      menuPlacement="bottom"
      menuPosition="fixed"
      menuPortalTarget={menuPortalTarget}
      menuShouldBlockScroll
      menuShouldScrollIntoView={false}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      onInputChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      ref={selectRef}
      {...rest}
    />
  );
}
